import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { USER_API } from "config";
import { useAxios } from "hooks/useAxios";
import { workspaceKeys } from "queries";
import { handleToast, ServerError } from "utils";
import { getToastMessageForError } from "utils/errorHandling";
import { subscribeMcpConnectionsChanged } from "utils/mcpConnectionEvents";

/** An AI app connected to this workspace through OAuth (one grant). */
export type McpConnection = {
  grant_id: string;
  workspace_id: string;
  user_id: string;
  client_id: string;
  client_name: string | null;
  scopes: string[];
  status: string;
  created_at: string;
  activated_at: string | null;
  last_used_at: string | null;
  expires_at: string;
  /** Who approved it. Present on the workspace-wide admin list. */
  owner?: { first_name: string | null; last_name: string | null; email: string | null };
};

export const MCP_CAPABILITY_SCOPES_IN_ORDER = [
  "rivetai:read",
  "rivetai:write",
  "rivetai:ai",
  "rivetai:delete",
] as const;

export const MCP_CAPABILITY_LABELS: Record<string, string> = {
  "rivetai:read": "View",
  "rivetai:write": "Edit",
  "rivetai:ai": "Run AI",
  "rivetai:delete": "Delete",
};

export const connectionAppName = (connection: McpConnection): string => {
  if (connection.client_name?.trim()) return connection.client_name.trim();
  try {
    return new URL(connection.client_id).host;
  } catch {
    return "AI app";
  }
};

export const useMcpConnections = (
  workspaceId: string | undefined,
  view: "mine" | "all",
  { poll = true }: { poll?: boolean } = {}
) => {
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const queryKey = workspaceKeys.mcpConnections(workspaceId ?? "", view);

  const connectionsQuery = useQuery<McpConnection[]>({
    queryKey,
    queryFn: async () => {
      const url =
        view === "all"
          ? USER_API.getAllWorkspaceMcpConnections(workspaceId!)
          : USER_API.getWorkspaceMcpConnections(workspaceId!);
      const response = await axios.get<{ data: McpConnection[] }>(url);
      return response.data.data;
    },
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: true,
    // Connecting finishes in another app, so keep the list current while this
    // page is open. React Query pauses the interval in background tabs.
    // Always-on chrome (the header) passes poll: false and polls only while
    // its dialog is open.
    refetchInterval: poll ? 15_000 : false,
  });

  // Approving on the consent page (another tab) refreshes this list right away.
  // The AI app finishes its token exchange a moment later, which is when the
  // connection becomes active, so check again shortly after.
  const refetch = connectionsQuery.refetch;
  useEffect(() => {
    if (!workspaceId) return;
    const timers: number[] = [];
    const unsubscribe = subscribeMcpConnectionsChanged(() => {
      void refetch();
      timers.push(window.setTimeout(() => void refetch(), 2_000));
      timers.push(window.setTimeout(() => void refetch(), 6_000));
    });
    return () => {
      unsubscribe();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [workspaceId, refetch]);

  const revokeMutation = useMutation({
    /** One app can hold several connections (Claude web and Claude Code); revoke them together. */
    mutationFn: async (grantIds: string[]) => {
      await Promise.all(
        grantIds.map((grantId) =>
          axios.delete(USER_API.revokeWorkspaceMcpConnection(workspaceId!, grantId))
        )
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.mcpConnections(workspaceId ?? "", "mine"),
      });
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.mcpConnections(workspaceId ?? "", "all"),
      });
      handleToast("App disconnected. It can no longer use this workspace.", "success");
    },
    onError: (error: unknown) => {
      const serverError = isAxiosError(error) ? ServerError.fromAxiosError(error) : null;
      handleToast(
        serverError ? getToastMessageForError(serverError) : "Could not disconnect the app.",
        "error"
      );
    },
  });

  return { connectionsQuery, revokeMutation };
};
